import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Languages, Plus, RefreshCw, Upload } from 'lucide-react';
import { Button, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { useTargets } from '../../identity/session';
import { saveBlob, useBuddyWrite } from '../api/client';
import { DEFAULT_LANGUAGES, translationsFor, type TranslationEntry } from '../api/translations';
import { useUrlParams } from './quality/params';
import '../buddy.css';
import './translations.css';

const entryKey = (e: TranslationEntry) => `${e.namespace}.${e.key}`;

/** One text: edited in place, saved on blur or Enter, Escape restores it. */
function TextCell({
  entry,
  canWrite,
  onSave,
}: {
  entry: TranslationEntry;
  canWrite: boolean;
  onSave: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState(entry.text);
  const [busy, setBusy] = useState(false);
  useEffect(() => setText(entry.text), [entry.text]);
  if (!canWrite) return entry.text ? <span>{entry.text}</span> : <span className="nyk-ui-hint">missing</span>;
  const commit = async () => {
    if (text === entry.text) return;
    setBusy(true);
    try {
      await onSave(text);
    } catch {
      setText(entry.text);
    } finally {
      setBusy(false);
    }
  };
  return (
    <input
      className="nyk-ui-input"
      aria-label={entryKey(entry)}
      value={text}
      placeholder={entry.fallback}
      disabled={busy}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') void commit();
        else if (e.key === 'Escape') setText(entry.text);
      }}
    />
  );
}

function AddKeyDialog({
  language,
  namespace,
  namespaces,
  onClose,
  onSave,
}: {
  language: string;
  namespace: string;
  namespaces: string[];
  onClose: () => void;
  onSave: (v: { namespace: string; key: string; text: string }) => Promise<void>;
}) {
  const [v, setV] = useState({ namespace, key: '', text: '' });
  const [busy, setBusy] = useState(false);
  const valid = !!v.namespace.trim() && !!v.key.trim();
  const save = async () => {
    setBusy(true);
    try {
      await onSave({ namespace: v.namespace.trim(), key: v.key.trim(), text: v.text });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Add a key (${language})`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid} loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="nyk-form">
        <TextField
          label="Namespace"
          value={v.namespace}
          list="bd-namespaces"
          onChange={(e) => setV({ ...v, namespace: e.target.value })}
        />
        <datalist id="bd-namespaces">
          {namespaces.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <TextField label="Key" value={v.key} onChange={(e) => setV({ ...v, key: e.target.value })} />
        <TextField
          label="Text"
          fieldClassName="nyk-span2"
          value={v.text}
          onChange={(e) => setV({ ...v, text: e.target.value })}
        />
      </div>
    </Dialog>
  );
}

/** nyce.buddy's UI texts per language: missing keys fall back to English, auto-translate fills them with the model. */
export function BuddyTranslationsPage() {
  const { active } = useTargets();
  const canWrite = useBuddyWrite();
  const { get, set } = useUrlParams();
  const language = get('language') || 'sv';
  const namespace = get('namespace');
  const missingOnly = get('missing') === '1';
  const search = get('q');
  const api = translationsFor(active);
  const languages = useAsync(() => api.languages().catch(() => DEFAULT_LANGUAGES), [active]);
  // The namespace is filtered here, not by buddy3, so the namespace list stays complete while one is picked.
  const entries = useAsync(
    () => api.list({ language, missingOnly: missingOnly || undefined }),
    [active, language, missingOnly]
  );
  const toast = useNotice();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<'auto' | 'import' | 'export' | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const all = useMemo(() => entries.data ?? [], [entries.data]);
  const namespaces = useMemo(() => [...new Set(all.map((e) => e.namespace))].sort(), [all]);
  const visible = useMemo(() => {
    const q = search.toLowerCase();
    return all.filter(
      (e) =>
        (!namespace || e.namespace === namespace) &&
        (!q || `${entryKey(e)} ${e.text} ${e.fallback ?? ''}`.toLowerCase().includes(q))
    );
  }, [all, namespace, search]);
  const missing = visible.filter((e) => e.missing).length;
  const langOptions = (languages.data ?? DEFAULT_LANGUAGES).map((l) => ({
    value: l.code,
    label: `${l.name} (${l.code})`,
  }));
  if (!langOptions.some((o) => o.value === language)) langOptions.push({ value: language, label: language });

  const act = async (kind: 'auto' | 'import' | 'export', fn: () => Promise<string | void>) => {
    setBusy(kind);
    try {
      const done = await fn();
      if (done) toast.success(done);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const save = async (namespaceName: string, key: string, text: string) => {
    try {
      await api.upsert({ languageCode: language, namespace: namespaceName, key, text });
      await entries.reload();
    } catch (e) {
      toast.error((e as Error).message);
      throw e;
    }
  };
  const importFile = (file: File) =>
    act('import', async () => {
      let bundle: unknown;
      try {
        bundle = JSON.parse(await file.text());
      } catch {
        throw new Error(`${file.name} is not a JSON file.`);
      }
      const r = await api.importBundle(bundle);
      await entries.reload();
      return `Imported ${r.imported} entries from ${file.name}`;
    });

  return (
    <Page
      title="Translations"
      description="nyce.buddy's UI texts per language. Missing keys fall back to English; auto-translate fills the gaps with the model."
      actions={
        <div className="bd-toolbar">
          <Select
            aria-label="Language"
            value={language}
            onChange={(v) => set({ language: v === 'sv' ? null : v })}
            options={langOptions}
          />
          <Select
            aria-label="Namespace"
            value={namespace}
            onChange={(v) => set({ namespace: v })}
            options={[
              { value: '', label: 'All namespaces' },
              ...(namespace && !namespaces.includes(namespace) ? [namespace] : []).map((n) => ({
                value: n,
                label: n,
              })),
              ...namespaces.map((n) => ({ value: n, label: n })),
            ]}
          />
          <Switch
            checked={missingOnly}
            onChange={(c) => set({ missing: c ? '1' : null })}
            label="Missing only"
          />
          <input
            className="nyk-ui-input nyk-search"
            type="search"
            aria-label="Search"
            placeholder="Search keys and texts"
            value={search}
            onChange={(e) => set({ q: e.target.value })}
          />
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={entries.loading}
            onClick={() => void entries.reload()}
          >
            Refresh
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<Download size={14} />}
            loading={busy === 'export'}
            onClick={() =>
              void act('export', async () => {
                const f = await api.exportFile(language);
                saveBlob(f.blob, f.fileName);
              })
            }
          >
            Export
          </Button>
          {canWrite && (
            <>
              <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={() => setAdding(true)}>
                Add key
              </Button>
              <ConfirmButton
                size="sm"
                variant="ghost"
                icon={<Languages size={14} />}
                loading={busy === 'auto'}
                disabled={language === 'en'}
                message={`Translate every missing ${language} text${namespace ? ` in ${namespace}` : ''} with the model?`}
                onConfirm={() =>
                  void act('auto', async () => {
                    const r = await api.autoTranslate(language, namespace || undefined);
                    await entries.reload();
                    return `Auto-translated ${r.translated} entries`;
                  })
                }
              >
                Auto-translate missing
              </ConfirmButton>
              <Button
                size="sm"
                variant="ghost"
                icon={<Upload size={14} />}
                loading={busy === 'import'}
                onClick={() => fileRef.current?.click()}
              >
                Import
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                hidden
                data-testid="import-file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void importFile(file);
                }}
              />
            </>
          )}
        </div>
      }
    >
      {toast.element}
      <ErrorBox error={entries.error} />
      <Loading loading={entries.loading && !entries.data} />
      {entries.data && (
        <div className="nyk-card">
          <div className="nyk-card-head">
            <h2>{langOptions.find((o) => o.value === language)?.label ?? language}</h2>
            <span className="nyk-ui-hint" data-testid="translations-count">
              {visible.length} keys{missing ? ` · ${missing} missing` : ''}
            </span>
          </div>
          <TableScroller>
            <table className="nyk-ui-table nyk-cards bd-translations" data-testid="translations-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>English</th>
                  <th>{language}</th>
                  {canWrite && <th />}
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => (
                  <tr key={entryKey(e)} data-key={entryKey(e)} data-missing={e.missing || undefined}>
                    <td className="nyk-card-title">
                      <span className="nyk-mono">
                        <span className="nyk-ui-hint">{e.namespace}.</span>
                        {e.key}
                      </span>
                    </td>
                    <td data-label="English" className="nyk-ui-hint">
                      {e.fallback}
                    </td>
                    <td data-label={language}>
                      <TextCell
                        entry={e}
                        canWrite={canWrite}
                        onSave={(text) => save(e.namespace, e.key, text)}
                      />
                    </td>
                    {canWrite && (
                      <td className="nyk-row-actions">
                        {e.id != null && (
                          <ConfirmButton
                            size="sm"
                            variant="ghost"
                            message={`Delete the ${language} text of ${entryKey(e)}? The English fallback is shown instead.`}
                            onConfirm={() =>
                              void api
                                .remove(e.id!)
                                .then(() => entries.reload())
                                .then(() => toast.success(`Deleted ${entryKey(e)}`))
                                .catch((err: Error) => toast.error(err.message))
                            }
                          >
                            Delete
                          </ConfirmButton>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
          {!visible.length && (
            <EmptyState
              title="Nothing to show."
              hint={missingOnly ? 'Every key is translated.' : 'No key matches the filters.'}
            />
          )}
        </div>
      )}
      {adding && (
        <AddKeyDialog
          language={language}
          namespace={namespace || 'chat'}
          namespaces={namespaces}
          onClose={() => setAdding(false)}
          onSave={async (v) => {
            await save(v.namespace, v.key, v.text);
            setAdding(false);
            toast.success(`Added ${v.namespace}.${v.key}`);
          }}
        />
      )}
    </Page>
  );
}
